import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { DatePicker } from './DatePicker';
import { LoungeLogo } from './LoungeLogo';
import { PersonalDeviceTimeSlotPicker } from './PersonalDeviceTimeSlotPicker';
import { RESERVATION_DATE_LIMIT } from '../../constants/reservation';
import { ROUTE_PATH } from '../../constants/routes';
import { API_ENDPOINTS, useApiRequest } from '../../hooks/useApiRequest';
import { useAreas } from '../../hooks/useAreas';
import { getShowerBaseDate } from '../../utils/businessDate';
import { toDateString } from '../../utils/datetime';
import { ErrorNotice } from '../common/ErrorNotice';
import type { ReservationFormData } from './ReservationFormServiceTypeSelector';
import type { API } from '@/api';
import type { ReservationRestoreData } from '@/hooks/useRestoreReservation';
import { AIRPORTS } from '@/constants/area';
import { SPACE_TYPE } from '@/constants/space';
import { useConvertToDate } from '@/hooks/useCovertToDate';
import { useDatePicker } from '@/hooks/useDatePicker';
import { useDateTimeFormat } from '@/hooks/useDateTimeFormat';

type ShowerRoomTimeSlot =
  API['GET']['ShowerRoomAvailableTimeSlots']['response']['time_slots'][number];

type StepName = 'airport' | 'terminal' | 'lounge' | 'datetime' | '';

interface Props {
  restoreData?: ReservationRestoreData;
}

export const ReservationFormShowerRoom = ({
  restoreData: initialRestoreData,
}: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { formatDateWithWeekday } = useDateTimeFormat();
  const { extractDateFromISO8601, extractTimeFromISO8601, formatToJstIso8601 } =
    useConvertToDate();

  // 各ステップの開閉状態
  const [openStep, setOpenStep] = useState<StepName>('airport');

  const [availableTimeSlots, setAvailableTimeSlots] = useState<
    ShowerRoomTimeSlot[]
  >([]);

  // 復元中かどうかを追跡するフラグ
  const isRestoringRef = useRef(false);

  const { handleSubmit, watch, setValue } = useForm<ReservationFormData>({
    mode: 'onChange',
    defaultValues: {
      serviceType: SPACE_TYPE.SHOWER_ROOM,
      airport: '',
      flightType: '',
      terminal: '',
      lounge: '',
      areaIds: [],
      boardingDate: undefined,
    },
  });

  const selectedAirport = watch('airport');
  const selectedFlightType = watch('flightType');
  const selectedTerminal = watch('terminal');
  const selectedLounge = watch('lounge');
  const boardingDate = watch('boardingDate');
  const selectedStartTime = watch('selectedStartTime');
  const selectedEndTime = watch('selectedEndTime');

  const {
    airports,
    terminals,
    lounges,
    isLoadingAirports,
    isLoadingTerminals,
    isLoadingLounges,
    error,
    fetchAirports,
    fetchTerminals,
    fetchLounges,
  } = useAreas();

  const { execute: fetchShowerRoomTimeSlots } = useApiRequest(
    'GET',
    API_ENDPOINTS.GET.SHOWER_ROOM_AVAILABLE_TIME_SLOTS,
  );

  // 初回マウント時に空港リストを取得
  useEffect(() => {
    fetchAirports(SPACE_TYPE.SHOWER_ROOM);
  }, [fetchAirports]);

  // Stage 1: 空港・便種を設定し、ターミナルを取得
  useEffect(() => {
    if (
      initialRestoreData?.serviceType === SPACE_TYPE.SHOWER_ROOM &&
      !isRestoringRef.current &&
      initialRestoreData?.airport &&
      initialRestoreData?.flightType
    ) {
      isRestoringRef.current = true;

      setValue('airport', initialRestoreData.airport);
      setValue('flightType', initialRestoreData.flightType);

      fetchTerminals(
        SPACE_TYPE.SHOWER_ROOM,
        initialRestoreData.airport,
        initialRestoreData.flightType,
      );
    }
  }, [initialRestoreData, setValue, fetchTerminals]);

  // Stage 2: terminalsがロードされたらターミナルを設定し、ラウンジを取得
  useEffect(() => {
    if (
      initialRestoreData?.serviceType === SPACE_TYPE.SHOWER_ROOM &&
      isRestoringRef.current &&
      initialRestoreData?.terminal &&
      terminals.length > 0 &&
      !selectedTerminal
    ) {
      setValue('terminal', initialRestoreData.terminal);

      fetchLounges(
        SPACE_TYPE.SHOWER_ROOM,
        initialRestoreData.airport!,
        initialRestoreData.flightType!,
        initialRestoreData.terminal,
      );
    }
  }, [initialRestoreData, terminals, selectedTerminal, setValue, fetchLounges]);

  // Stage 3: loungesがロードされたらラウンジを設定
  useEffect(() => {
    if (
      initialRestoreData?.serviceType === SPACE_TYPE.SHOWER_ROOM &&
      isRestoringRef.current &&
      initialRestoreData?.lounge &&
      lounges.length > 0 &&
      !selectedLounge
    ) {
      setValue('lounge', initialRestoreData.lounge);

      isRestoringRef.current = false;
    }
  }, [initialRestoreData, lounges, selectedLounge, setValue]);

  // フォームの値を指定したステップ以降で初期化
  const resetFormFrom = useCallback(
    (step: 'terminal' | 'lounge' | 'datetime') => {
      if (step === 'terminal') {
        setValue('terminal', '');
      }
      if (step === 'terminal' || step === 'lounge') {
        setValue('lounge', '');
        setValue('areaIds', []);
      }
      setValue('boardingDate', undefined);
      setValue('selectedStartTime', undefined);
      setValue('selectedEndTime', undefined);
      setAvailableTimeSlots([]);
    },
    [setValue],
  );

  // 空港が選択されたらターミナルリストを取得し、次のstepを開く
  const handleAirportChange = useCallback(
    (airport: string, flightType: string) => {
      setValue('airport', airport);
      setValue('flightType', flightType);
      resetFormFrom('terminal');
      setOpenStep('terminal');
      fetchTerminals(SPACE_TYPE.SHOWER_ROOM, airport, flightType);
    },
    [setValue, resetFormFrom, fetchTerminals],
  );

  // 空港が1つだけの場合は自動選択
  useEffect(() => {
    if (
      airports.length === 1 &&
      !selectedAirport &&
      !isRestoringRef.current // 復元中はスキップ
    ) {
      const singleAirport = airports[0];
      handleAirportChange(singleAirport.airport, singleAirport.flightType);
    }
  }, [airports, selectedAirport, handleAirportChange]);

  // ターミナルが1つだけの場合は自動選択
  useEffect(() => {
    if (
      terminals.length === 1 &&
      !selectedTerminal &&
      selectedAirport &&
      !isRestoringRef.current // 復元中はスキップ
    ) {
      setValue('terminal', terminals[0]);
    }
  }, [terminals, selectedTerminal, selectedAirport, setValue]);

  // ターミナルが選択されたらラウンジリストを取得し、次のstepを開く
  useEffect(() => {
    if (
      selectedTerminal &&
      selectedAirport &&
      selectedFlightType &&
      !isRestoringRef.current // 復元中はスキップ
    ) {
      resetFormFrom('lounge');
      setOpenStep('lounge');
      fetchLounges(
        SPACE_TYPE.SHOWER_ROOM,
        selectedAirport,
        selectedFlightType,
        selectedTerminal,
      );
    }
  }, [
    selectedTerminal,
    selectedAirport,
    selectedFlightType,
    fetchLounges,
    resetFormFrom,
  ]);

  // アクセス可能なラウンジのリスト
  const accessibleLounges = useMemo(
    () => lounges.filter((l) => l.accessible),
    [lounges],
  );

  // ドロップダウン展開可能かどうか（選択肢が複数あるか、ローディング中か）
  const canExpandAirport = airports.length > 1 || isLoadingAirports;
  const canExpandTerminal = terminals.length > 1 || isLoadingTerminals;
  const canExpandLounge = accessibleLounges.length > 1 || isLoadingLounges;

  // アクセス可能なラウンジが1つだけの場合は自動選択
  useEffect(() => {
    if (
      accessibleLounges.length === 1 &&
      !selectedLounge &&
      selectedTerminal &&
      !isRestoringRef.current // 復元中はスキップ
    ) {
      const singleLounge = accessibleLounges[0];
      setValue('lounge', singleLounge.lounge);
      setValue('areaIds', singleLounge.areaIds);
    }
  }, [accessibleLounges, selectedLounge, selectedTerminal, setValue]);

  // 日付選択時に時間帯を取得するハンドラー
  const handleDateSelect = useCallback(
    async (date: string) => {
      setValue('boardingDate', date as `${number}-${number}-${number}`);
      const areaIds = watch('areaIds');

      if (!areaIds || !date) {
        return;
      }

      const timeSlotParams = {
        area_ids: areaIds,
        date: date,
      };

      const result = await fetchShowerRoomTimeSlots(timeSlotParams);
      if (result.ok) {
        setAvailableTimeSlots(result.data.time_slots);
      } else {
        console.error(
          'Failed to fetch shower room time slots:',
          result.error,
          result.details,
        );
        setAvailableTimeSlots([]);
      }
    },
    [fetchShowerRoomTimeSlots, setValue, watch],
  );

  const { currentMonth, changeMonth, isValidMonth, setCurrentMonth } =
    useDatePicker({
      maxDaysFromToday: RESERVATION_DATE_LIMIT.SHOWER,
      onDateSelect: handleDateSelect,
    });

  // ラウンジが選択されたらareaIdsを設定し、日時stepを開く
  useEffect(() => {
    if (
      selectedLounge &&
      !isRestoringRef.current // 復元中はスキップ
    ) {
      const selectedLoungeData = lounges.find(
        (l) => l.lounge === selectedLounge,
      );
      if (selectedLoungeData) {
        setValue('areaIds', selectedLoungeData.areaIds);
      }
      resetFormFrom('datetime');
      setOpenStep('datetime');
      // デフォルトの搭乗日を設定
      handleDateSelect(
        toDateString(
          getShowerBaseDate(
            selectedAirport,
            selectedFlightType,
            selectedTerminal,
          ),
        ),
      );
    }
  }, [
    selectedLounge,
    lounges,
    setValue,
    resetFormFrom,
    handleDateSelect,
    selectedAirport,
    selectedFlightType,
    selectedTerminal,
  ]);

  // 時間スロット選択時のハンドラー
  const handleTimeSelect = (startDateTime: string, durationMinutes: number) => {
    const endDate = new Date(
      new Date(startDateTime).getTime() + durationMinutes * 60 * 1000,
    );
    const endDateTime = formatToJstIso8601(endDate);
    setValue('selectedStartTime', startDateTime, { shouldValidate: true });
    setValue('selectedEndTime', endDateTime, {
      shouldValidate: true,
    });
  };

  const onSubmit = (data: ReservationFormData) => {
    // startDateTimeから日付を抽出（深夜帯の場合、DatePickerの日付と異なる可能性があるため）
    const slotDate = data.selectedStartTime
      ? extractDateFromISO8601(data.selectedStartTime)
      : data.boardingDate;

    const reservationData = {
      serviceType: SPACE_TYPE.SHOWER_ROOM,
      airport: data.airport,
      flightType: data.flightType,
      terminal: data.terminal,
      lounge: data.lounge,
      areaIds: data.areaIds,
      boardingDate: slotDate,
      startDateTime: data.selectedStartTime,
      endDateTime: data.selectedEndTime,
    };

    navigate(ROUTE_PATH.RESERVATION_CONFIRM, {
      state: {
        reservationData,
      },
    });
  };

  // 各ステップの完了状態
  const isStep1Complete = !!selectedAirport && !!selectedFlightType;
  const isStep2Complete = !!selectedTerminal;
  const isStep3Complete = !!selectedLounge;
  const isStep4Complete = !!selectedEndTime;

  // 確定済み日時の表示文字列
  const confirmedDateStr: string = useMemo(() => {
    if (!selectedStartTime || !selectedEndTime) {
      return '';
    }

    // selectedStartTimeから日付・時刻を抽出
    const slotDate = extractDateFromISO8601(selectedStartTime);
    const dateWithWeekday = formatDateWithWeekday(slotDate);
    const startTime = extractTimeFromISO8601(selectedStartTime);
    const endTime = extractTimeFromISO8601(selectedEndTime);

    return `${dateWithWeekday} ${startTime}${t('reservation.confirm.timeRange')}${endTime}`;
  }, [
    selectedStartTime,
    selectedEndTime,
    extractDateFromISO8601,
    extractTimeFromISO8601,
    formatDateWithWeekday,
    t,
  ]);

  // 空港の表示名を取得
  const getAirportDisplayName = () => {
    if (!selectedAirport || !selectedFlightType) {
      return t('reservation.airport.placeholder');
    }
    return `${t(`common.airport.${selectedAirport}`)}`;
  };

  // ターミナルの表示名を取得
  const getTerminalDisplayName = () => {
    if (!selectedTerminal) {
      return t('reservation.terminal.placeholder');
    }
    // 成田空港はフライトタイプを省略
    if (selectedAirport === AIRPORTS.NARITA) {
      return t(`common.terminal.${selectedTerminal}`);
    }
    return t('common.terminalWithFlightType', {
      terminal: t(`common.terminal.${selectedTerminal}`),
      flightType: t(`common.flightType.${selectedFlightType}`),
    });
  };

  // ラウンジの表示名を取得
  const getLoungeDisplayName = () => {
    if (!selectedLounge) {
      return t('reservation.lounge.placeholder');
    }
    return t(`common.lounge.${selectedLounge}`);
  };

  if (error) {
    return <ErrorNotice message={error} />;
  }

  return (
    <form className="reservation-card__form" onSubmit={handleSubmit(onSubmit)}>
      {/* Step 1: 空港 */}
      <div
        className={`reservation-card__step reservation-card__step--airport step1
          ${isStep1Complete ? 'is-complete' : ''}
          ${openStep === 'airport' && canExpandAirport ? 'is-open' : ''}
          ${!canExpandAirport ? 'is-disabled-dropdown' : ''}`}
        data-step="1"
      >
        <div
          className="reservation-card__step-header"
          style={{ cursor: canExpandAirport ? 'pointer' : 'not-allowed' }}
          onClick={() => canExpandAirport && setOpenStep('airport')}
        >
          <div className="reservation-card__step-label">
            {isStep1Complete
              ? getAirportDisplayName()
              : t('reservation.airport.title')}
          </div>
        </div>
        {openStep === 'airport' && canExpandAirport && (
          <div className="reservation-card__step-body">
            <div className="reservation-card__dropdown is-open">
              <div className="reservation-card__dropdown-panel">
                {isLoadingAirports ? (
                  <div className="reservation-card__dropdown-loading">
                    Loading...
                  </div>
                ) : (
                  airports.map((airportFlightType) => (
                    <button
                      key={`${airportFlightType.airport}:${airportFlightType.flightType}`}
                      type="button"
                      className="reservation-card__dropdown-option"
                      data-value={airportFlightType.airport}
                      onClick={() => {
                        handleAirportChange(
                          airportFlightType.airport,
                          airportFlightType.flightType,
                        );
                      }}
                    >
                      {t(`common.airport.${airportFlightType.airport}`)}
                    </button>
                  ))
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Step 2: ターミナル */}
      <div
        className={`reservation-card__step reservation-card__step--terminal step2
          ${isStep2Complete ? 'is-complete is-terminal-selected' : ''}
          ${isStep1Complete ? 'is-terminal-selected' : 'is-disabled'}
          ${openStep === 'terminal' && canExpandTerminal ? 'is-open' : ''}
          ${!canExpandTerminal ? 'is-disabled-dropdown' : ''}`}
        data-step="2"
      >
        <div
          className="reservation-card__step-header"
          style={{
            cursor:
              isStep1Complete && canExpandTerminal ? 'pointer' : 'not-allowed',
          }}
          onClick={() =>
            isStep1Complete && canExpandTerminal && setOpenStep('terminal')
          }
        >
          <div className="reservation-card__step-label">
            {isStep2Complete
              ? getTerminalDisplayName()
              : t('reservation.terminal.title')}
          </div>
        </div>
        {isStep1Complete && openStep === 'terminal' && canExpandTerminal && (
          <div className="reservation-card__step-body">
            <div className="reservation-card__dropdown is-open">
              <div className="reservation-card__dropdown-panel">
                {isLoadingTerminals ? (
                  <div className="reservation-card__dropdown-loading">
                    Loading...
                  </div>
                ) : (
                  terminals.map((terminalName) => (
                    <button
                      key={terminalName}
                      type="button"
                      className="reservation-card__dropdown-option"
                      data-value={terminalName}
                      onClick={() => {
                        setValue('terminal', terminalName);
                      }}
                    >
                      {selectedAirport === AIRPORTS.NARITA
                        ? t(`common.terminal.${terminalName}`)
                        : t('common.terminalWithFlightType', {
                            terminal: t(`common.terminal.${terminalName}`),
                            flightType: t(
                              `common.flightType.${selectedFlightType}`,
                            ),
                          })}
                    </button>
                  ))
                )}
              </div>
            </div>
          </div>
        )}
        <a
          href={t('reservation.terminal.linkUrlShower')}
          target="_blank"
          rel="noopener noreferrer"
          className="reservation-card__note--link reservation-card__note--pointer"
        >
          {t('reservation.terminal.linkText')}
        </a>
      </div>

      {/* Step 3: ラウンジ */}
      <div
        className={`
          reservation-card__step reservation-card__step--lounge step3
          ${isStep3Complete ? 'is-complete is-lounge-selected' : ''}
          ${isStep2Complete ? 'is-lounge-selected' : 'is-disabled'}
          ${openStep === 'lounge' && canExpandLounge ? 'is-open' : ''}
          ${!canExpandLounge ? 'is-disabled-dropdown' : ''}`}
        data-step="3"
      >
        <div
          className="reservation-card__step-header"
          style={{
            cursor:
              isStep2Complete && canExpandLounge ? 'pointer' : 'not-allowed',
          }}
          onClick={() =>
            isStep2Complete && canExpandLounge && setOpenStep('lounge')
          }
        >
          <div
            className="reservation-card__step-label"
            data-initial-label={t('reservation.lounge.label')}
          >
            {/* ラウンジロゴ */}
            {/* NRT5サテの場合、ラウンジが共有のため、SLとALの表示を同時に出す */}
            {isStep3Complete ? (
              selectedLounge ? (
                <LoungeLogo
                  lounge={selectedLounge}
                  className="reservation-card__lounge-label-img reservation-card__lounge-label-img--suite"
                  altText={getLoungeDisplayName()}
                />
              ) : (
                getLoungeDisplayName()
              )
            ) : (
              t('reservation.lounge.title')
            )}
          </div>
        </div>
        {isStep2Complete && openStep === 'lounge' && canExpandLounge && (
          <div className="reservation-card__step-body">
            <div className="reservation-card__dropdown is-open">
              <div className="reservation-card__dropdown-panel">
                {isLoadingLounges ? (
                  <div className="reservation-card__dropdown-loading">
                    Loading...
                  </div>
                ) : (
                  accessibleLounges.map((loungeOption) => (
                    <button
                      key={loungeOption.lounge}
                      type="button"
                      className="reservation-card__dropdown-option"
                      data-value={loungeOption.lounge}
                      onClick={() => {
                        setValue('lounge', loungeOption.lounge);
                        setValue('areaIds', loungeOption.areaIds);
                      }}
                    >
                      {loungeOption.lounge === 'suite_lounge' ? (
                        <img
                          className="reservation-card__lounge-img reservation-card__lounge-img--suite"
                          src="/B_2_ANA_LOUNGE_Long_Long.png"
                          alt="ANA SUITE LOUNGE"
                        />
                      ) : loungeOption.lounge === 'ana_lounge' ? (
                        <img
                          className="reservation-card__lounge-img reservation-card__lounge-img--ana"
                          src="/B_2_ANA_LOUNGE_Long.png"
                          alt="ANA LOUNGE"
                        />
                      ) : (
                        t(`common.lounge.${loungeOption.lounge}`)
                      )}
                    </button>
                  ))
                )}
              </div>
            </div>
          </div>
        )}
        <a
          href={t('reservation.lounge.linkUrlShower')}
          target="_blank"
          rel="noopener noreferrer"
          className="reservation-card__link"
        >
          {t('reservation.lounge.linkText')}
        </a>
      </div>

      {/* Step 4: 搭乗日・時間 */}
      <div
        className={`reservation-card__step reservation-card__step--datetime step4 ${
          openStep === 'datetime'
            ? 'is-open'
            : isStep4Complete
              ? 'is-active is-complete'
              : ''
        } ${!isStep3Complete ? 'is-disabled' : ''}`}
        data-step="4"
      >
        <div
          className="reservation-card__step-header"
          style={{ cursor: isStep3Complete ? 'pointer' : 'not-allowed' }}
          onClick={() => isStep3Complete && setOpenStep('datetime')}
        >
          <div className="reservation-card__step-label">
            {openStep !== 'datetime' && confirmedDateStr
              ? confirmedDateStr
              : t('reservation.boarding.dateAndTime')}
          </div>
        </div>

        {isStep3Complete && openStep === 'datetime' && (
          <div className="reservation-card__step-body">
            <div className="reservation-card__datetime">
              <DatePicker
                selectedDate={boardingDate}
                onDateSelect={handleDateSelect}
                className="date-picker date-picker__shower"
                maxDaysFromToday={RESERVATION_DATE_LIMIT.SHOWER}
                spaceType={SPACE_TYPE.SHOWER_ROOM}
                airport={selectedAirport}
                flightType={selectedFlightType}
                terminal={selectedTerminal}
                currentMonth={currentMonth}
                changeMonth={changeMonth}
                setCurrentMonth={setCurrentMonth}
              />
              <input
                type="hidden"
                name="reserve-datetime"
                data-step-input="4"
              />
            </div>
            <div className="reservation-card__info reservation-card__info--timezone">
              {t('reservation.boarding.timezoneNote')}
            </div>

            {/* 時間帯選択 */}
            {boardingDate && availableTimeSlots.length > 0 && isValidMonth && (
              <div className="shower-picker">
                <PersonalDeviceTimeSlotPicker
                  timeSlots={availableTimeSlots}
                  onTimeSelect={handleTimeSelect}
                  selectedDatetime={selectedStartTime}
                />
                <div className="shower-picker__actions">
                  <button
                    className={`shower-picker__action shower-picker__action--confirm ${!selectedStartTime ? 'is-disabled' : ''}`}
                    type="button"
                    disabled={!selectedStartTime}
                    onClick={() => setOpenStep('')}
                  >
                    {t('reservation.showerRoom.confirmTime')}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* 確認ボタン */}
      {openStep !== 'datetime' && isStep4Complete && (
        <div className="reservation-card__confirm reservation-card__confirm--shower">
          <button type="submit" className="reservation-card__confirm-button">
            {t('reservation.confirmContent')}
          </button>
          <p className="reservation-card__confirm-note">
            {t('reservation.confirm.note')}
          </p>
        </div>
      )}
    </form>
  );
};
